import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { BellIcon, BellOffIcon, SettingsIcon } from "lucide-react";
import { useNotifications } from "../../contexts/NotificationsContext";
import { NotificationItem } from "./NotificationItem";
import { spring } from "../../utils/motion";
interface NotificationBellProps {
  align?: "left" | "right";
}
export function NotificationBell({
  align = "right"
}: NotificationBellProps) {
  const {
    items,
    unreadCount,
    markRead,
    markAllRead
  } = useNotifications();
  const reduce = useReducedMotion() ?? false;
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"all" | "unread">("all");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const list = (tab === "unread" ? items.filter(n => !n.read) : items).slice(0, 5);
  return <div ref={ref} className="relative" data-id="element-853" data-mp="3s5ahztm9gvvj">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-haspopup="dialog" aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : "Notifications"} className="focus-ring relative grid h-10 w-10 place-items-center rounded-xl border-2 border-line/70 bg-surface text-ink transition-colors duration-150 hover:bg-sunken" data-id="element-854" data-mp="3s497bq95qbec">
        <BellIcon className="h-[18px] w-[18px]" strokeWidth={2.25} aria-hidden="true" data-id="element-855" data-mp="eotc0xjcnbmd" />
        <AnimatePresence data-id="element-856" data-mp="anhiigxcu68w">
          {unreadCount > 0 && <motion.span key="badge" initial={reduce ? {
          opacity: 0
        } : {
          scale: 0.6,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={reduce ? {
          opacity: 0
        } : {
          scale: 0.6,
          opacity: 0
        }} transition={spring.bouncy} className="absolute -right-1.5 -top-1.5 grid h-5 min-w-[20px] place-items-center rounded-full border-2 border-surface bg-bad px-1 text-[10px] font-bold text-white" aria-hidden="true" data-id="element-857" data-mp="httryqgpdyfm">
              {unreadCount}
            </motion.span>}
        </AnimatePresence>
      </button>

      <AnimatePresence data-id="element-858" data-mp="1ahzbj9w0thdv">
        {open && <motion.div role="dialog" aria-label="Notifications" initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -6,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -4,
        scale: 0.98,
        transition: {
          duration: 0.12
        }
      }} transition={spring.stable} className={`absolute z-50 mt-2 w-[min(380px,calc(100vw-2rem))] rounded-card border-3 border-line bg-surface shadow-brutal ${align === "left" ? "left-0 origin-top-left" : "right-0 origin-top-right"}`} data-id="element-859" data-mp="1fg99fs94qw36">
            <div className="flex items-center justify-between gap-3 px-4 pb-2 pt-4" data-id="element-860" data-mp="1kmfre39i2yho">
              <h2 className="text-base font-bold text-ink" data-id="element-861" data-mp="2hc5iiyio8y66">Notifications</h2>
              <button type="button" onClick={markAllRead} disabled={unreadCount === 0} className="focus-ring rounded-md text-sm font-semibold text-brand disabled:text-ink-2/60" data-id="element-862" data-mp="2da2544cfzji9">
                Mark all read
              </button>
            </div>
            <div role="tablist" aria-label="Filter notifications" className="flex gap-4 border-b border-line/15 px-4" data-id="element-863" data-mp="ww28p3ct2bzr">
              {(["all", "unread"] as const).map(t => <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)} className={`focus-ring relative -mb-px py-2 text-sm font-semibold capitalize ${tab === t ? "text-ink" : "text-ink-2 hover:text-ink"}`} data-id="element-864" data-mp="2ftnfnw5f4bm2">
                  {t === "unread" ? `Unread (${unreadCount})` : "All"}
                  {tab === t && <motion.span layoutId="bell-tab" transition={spring.snappy} className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-ink" data-id="element-865" data-mp="2zfjrtykrkhb5" />}
                </button>)}
            </div>

            {list.length === 0 ? <div className="flex flex-col items-center px-6 py-10 text-center" data-id="element-866" data-mp="2uh02do6yone1">
                <BellOffIcon className="h-7 w-7 text-ink-2" aria-hidden="true" data-id="element-867" data-mp="1e3rzlshd33j6" />
                <p className="mt-3 text-[15px] font-semibold text-ink" data-id="element-868" data-mp="1j3nv13bztmn4">You're all caught up</p>
                <p className="mt-1 text-sm text-ink-2" data-id="element-869" data-mp="1bjdaz6qdv3gv">New reminders and reviews will show up here.</p>
              </div> : <ul className="max-h-[400px] space-y-1 overflow-y-auto p-2" data-id="element-870" data-mp="16jqzp2u47xap">
                {list.map(n => <NotificationItem key={n.id} item={n} compact onOpen={item => {
            markRead(item.id);
            setOpen(false);
          }} data-id="element-871" data-mp="1kjih7djubvtg" />)}
              </ul>}

            <div className="flex items-center justify-between border-t border-line/15 px-4 py-3" data-id="element-872" data-mp="11lfpgqzwrkry">
              <Link to="/app/notifications" onClick={() => setOpen(false)} className="focus-ring rounded-md text-sm font-semibold text-ink underline underline-offset-4" data-id="element-873" data-mp="wupff0gjetqx">
                See all notifications
              </Link>
              <Link to="/app/profile/settings#notifications" onClick={() => setOpen(false)} aria-label="Notification settings" title="Notification settings" className="focus-ring grid h-8 w-8 place-items-center rounded-lg text-ink-2 hover:bg-sunken hover:text-ink" data-id="element-874" data-mp="248b2yp6zm0uq">
                <SettingsIcon className="h-4 w-4" data-id="element-875" data-mp="2iwo21v1dfztg" />
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}